import { useEffect, useRef, useState } from "react";
import Swal from "sweetalert2";
import { brand } from "../../../config/theme";
import Modal from "../../../components/base/Modal";
import type { buildThemeClasses } from "../theme";
import type { Lead } from "../services/leadsService";
import { placeAiCall } from "../services/callsService";
import { announceCallProblem } from "../services/callFeedback";
import { leadPhone } from "../services/leadPhone";
import { fetchAgents, suggestAgentForLead, type Agent, type AgentSuggestion } from "../services/agentsService";

// ============================================================================
// CallLeadButton — the "Llamar" action that floats over a Nuevo lead card.
// Opens a small menu with two ways to reach the lead:
//   • Llamar yo      — the agent talks to the lead through the browser softphone
//                      (handled by the parent via `onStartHuman`).
//   • Llamar con IA  — an AI voice agent calls; the best agent is PRE-SELECTED
//                      (GET /crm/agents/suggest) and the user can override it.
// Both calls are recorded, transcribed and qualified server-side.
// ============================================================================

type TC = ReturnType<typeof buildThemeClasses>;

export default function CallLeadButton({
  lead,
  tc,
  onStartHuman,
}: {
  lead: Lead;
  tc: TC;
  onStartHuman: (lead: Lead) => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Close the menu on any outside click.
  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen]);

  const stop = (e: React.MouseEvent) => e.stopPropagation();

  return (
    <div ref={wrapRef} className="relative" onClick={stop}>
      <button
        onClick={(e) => {
          stop(e);
          setMenuOpen((o) => !o);
        }}
        title="Llamar al lead"
        className="flex items-center gap-1 rounded-md bg-primary-500 px-2 py-1 text-[11px] font-medium text-on-primary shadow-sm transition hover:bg-primary-400"
      >
        <i className="ri-phone-line" /> Llamar
      </button>

      {menuOpen && (
        <div
          className="absolute right-0 z-20 mt-1 w-52 overflow-hidden rounded-lg border shadow-lg border-line bg-card"
        >
          <button
            onClick={(e) => {
              stop(e);
              setMenuOpen(false);
              onStartHuman(lead);
            }}
            className={`flex w-full items-center gap-2 px-3 py-2 text-left text-xs ${tc.text} hover:bg-primary-500/10`}
          >
            <i className="ri-user-voice-line text-primary-500" />
            <span>
              <span className="block font-medium">Llamar yo</span>
              <span className={`text-[10px] ${tc.textSecondary}`}>Hablas tú por la plataforma</span>
            </span>
          </button>
          <button
            onClick={(e) => {
              stop(e);
              setMenuOpen(false);
              setAiOpen(true);
            }}
            className={`flex w-full items-center gap-2 border-t px-3 py-2 text-left text-xs ${tc.cardBorder} ${tc.text} hover:bg-primary-500/10`}
          >
            <i className="ri-robot-2-line text-primary-500" />
            <span>
              <span className="block font-medium">Llamar con IA</span>
              <span className={`text-[10px] ${tc.textSecondary}`}>Un agente de voz IA califica</span>
            </span>
          </button>
        </div>
      )}

      {aiOpen && (
        <Modal
          title="Llamar con un agente IA"
          onClose={() => setAiOpen(false)}
          themeClasses={tc}
          content={
            <AiCallPicker
              lead={lead}
              tc={tc}
              onClose={() => setAiOpen(false)}
            />
          }
        />
      )}
    </div>
  );
}

// --- AI agent picker --------------------------------------------------------

function AiCallPicker({
  lead,
  tc,
  onClose,
}: {
  lead: Lead;
  tc: TC;
  onClose: () => void;
}) {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [suggestion, setSuggestion] = useState<AgentSuggestion | null>(null);
  const [selectedId, setSelectedId] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [placing, setPlacing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [page, sug] = await Promise.all([
          fetchAgents({ type: "ai", status: "active" }),
          suggestAgentForLead(lead.id).catch(() => null),
        ]);
        if (!alive) return;
        setAgents(page.data);
        setSuggestion(sug);
        // Pre-select: the suggested agent, else the first provisioned one.
        const pre =
          (sug?.agentId && page.data.some((a) => a.id === sug.agentId) ? sug.agentId : "") ||
          page.data.find((a) => a.elevenLabsAgentId)?.id ||
          page.data[0]?.id ||
          "";
        setSelectedId(pre);
      } catch (e) {
        const err = e as { message?: string };
        if (alive) setError(err.message || "No se pudieron cargar los agentes");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [lead.id]);

  const selected = agents.find((a) => a.id === selectedId) || null;
  const isSuggested = suggestion?.agentId && selectedId === suggestion.agentId;

  const place = async () => {
    if (!selectedId) return;
    setPlacing(true);
    try {
      const call = await placeAiCall(lead.id, selectedId);
      onClose();
      // The endpoint answers 201 even when the provider refused the call, so
      // check the record before promising a transcript that will never arrive.
      if (!announceCallProblem(call)) {
        Swal.fire({
          icon: "success",
          title: "Llamada IA en curso",
          text: "El agente está llamando al lead. La transcripción y calificación aparecerán en Llamadas.",
          confirmButtonColor: brand.primary,
          timer: 4000,
        });
      }
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      Swal.fire({
        icon: "error",
        title: "No se pudo llamar",
        text: err.response?.data?.message || err.message || "Error al iniciar la llamada IA",
        confirmButtonColor: brand.primary,
      });
    } finally {
      setPlacing(false);
    }
  };

  const inputClass = `w-full rounded-lg border px-3 py-2 text-sm outline-none border-line bg-app text-content`;

  if (loading) {
    return (
      <div className={`py-8 text-center text-sm ${tc.textSecondary}`}>
        <i className="ri-loader-4-line mr-2 animate-spin" /> Buscando el mejor agente…
      </div>
    );
  }

  if (error) {
    return <p className="py-6 text-center text-sm text-red-500">{error}</p>;
  }

  if (agents.length === 0) {
    return (
      <div className={`py-6 text-center text-sm ${tc.textSecondary}`}>
        <i className="ri-robot-2-line mb-2 block text-3xl" />
        No hay agentes de IA activos. Crea uno en Configuración → Agentes.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className={`text-sm ${tc.textSecondary}`}>
        Llamando a <span className={`font-semibold ${tc.text}`}>{lead.name ?? lead.company ?? leadPhone(lead)}</span> para
        calificarlo. Elige el agente (ya pre-seleccionamos el más adecuado).
      </p>

      <div>
        <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>Agente IA</label>
        <select className={inputClass} value={selectedId} onChange={(e) => setSelectedId(e.target.value)}>
          {agents.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
              {suggestion?.agentId === a.id ? "  ⭐ recomendado" : ""}
              {!a.elevenLabsAgentId ? "  (sin voz vinculada)" : ""}
            </option>
          ))}
        </select>
      </div>

      {isSuggested && suggestion?.reason && (
        <div className="rounded-lg border border-primary-500/30 bg-primary-500/5 px-3 py-2 text-xs text-primary-600 dark:text-primary-400">
          <i className="ri-sparkling-2-line mr-1" />
          {suggestion.reason}
        </div>
      )}

      {selected && (
        <div className={`rounded-lg border p-3 ${tc.cardBorder} ${tc.cardBg}`}>
          <p className={`text-xs ${tc.textSecondary}`}>Objetivo</p>
          <p className={`text-sm ${tc.text}`}>{selected.objective || "Confirmar y calificar al lead."}</p>
          {!selected.elevenLabsAgentId && (
            <p className="mt-2 text-[11px] text-amber-600 dark:text-amber-400">
              <i className="ri-error-warning-line mr-1" />
              Este agente no tiene un agente de ElevenLabs vinculado; la llamada podría no realizarse.
            </p>
          )}
        </div>
      )}

      <div className="flex justify-end gap-2 pt-1">
        <button
          onClick={onClose}
          className={`rounded-lg border px-4 py-2 text-sm ${tc.border} ${tc.textSecondary} ${tc.buttonHover}`}
        >
          Cancelar
        </button>
        <button
          onClick={place}
          disabled={placing || !selectedId}
          className="flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-on-primary hover:bg-primary-400 disabled:opacity-50"
        >
          {placing ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-phone-line" />}
          Llamar con IA
        </button>
      </div>
    </div>
  );
}
